var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useMemo, memo } from 'react';
import TableComponent from '@widget/list/Tables/TableComponent';
import useToast from '@notifications/Toast/useToast';
import useAlert from '@notifications/Alerts/useAlerts';
// ============================================================================
// TYPE GUARDS
// ============================================================================
function isArrayData(props) {
    return Array.isArray(props.data);
}
function isFunctionData(props) {
    return typeof props.data === 'function' && props.data.length === 0;
}
function isPaginatedData(props) {
    return typeof props.data === 'function' && props.data.length > 0;
}
function isEditMode(props) {
    return !!props.onUpdateItem || !!props.onDeleteItem;
}
// ============================================================================
// ADAPTER FOR BACKWARD COMPATIBILITY
// ============================================================================
function adaptLegacyProps(props) {
    return {
        columns: props.columns,
        data: props.data,
        noDataText: props.noDataText,
        pageSize: 10,
        disablePagination: true,
        onAddItem: props.onAddItem,
        onUpdateItem: props.onUpdateItem,
        onDeleteItem: props.onDeleteItem,
        onReorderItems: props.onReorderItems,
        roles: props.roles,
        labels: props.labels,
        uxpContext: props.uxpContext
    };
}
// ============================================================================
// MAIN COMPONENT
// ============================================================================
const SimpleConfigurationTableComponent = (props) => {
    var _a, _b;
    const [loading, setLoading] = useState(props.loading || false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const toast = useToast();
    const alert = useAlert();
    const isEdit = isEditMode(props);
    const isPaginated = isPaginatedData(props);
    const isFunction = isFunctionData(props);
    const isArray = isArrayData(props);
    const loadData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            if (isArray) {
                setData(props.data);
                setTotal(props.total || props.data.length);
            }
            else if (isFunction) {
                const result = yield props.data();
                setData(result);
                setTotal(result.length);
            }
            // Paginated: TC handles data fetching directly
        }
        catch (error) {
            console.error('Failed to load data:', error);
            setData([]);
            setTotal(0);
        }
        finally {
            setLoading(false);
        }
    }), [isArray, isFunction, props]);
    useEffect(() => {
        if (!isPaginated)
            loadData();
    }, [loadData, isPaginated]);
    // Edit mode callbacks
    const cancelNewItem = useCallback(() => {
        setData(prev => prev.filter(item => !item.__newitem__));
    }, []);
    const addNewItem = useCallback(() => {
        setData(prev => [...prev, { __newitem__: true }]);
    }, []);
    const saveItem = useCallback((_index, item) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!isEdit || !props.onUpdateItem)
            return true;
        const { success, error } = yield props.onUpdateItem(item);
        if (success) {
            if (!props.disableNotifications)
                toast.success(((_a = props.labels) === null || _a === void 0 ? void 0 : _a.saved) || 'Item saved successfully');
            return true;
        }
        else {
            if (!props.disableNotifications)
                alert.error(error || 'Unable to save. Something went wrong');
            return false;
        }
    }), [isEdit, props, toast, alert]);
    const saveNewItem = useCallback((_index, item) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!isEdit || !props.onAddItem)
            return true;
        const { success, error } = yield props.onAddItem(item);
        if (success) {
            if (!props.disableNotifications)
                toast.success(((_a = props.labels) === null || _a === void 0 ? void 0 : _a.saved) || 'Item saved successfully');
            if (!isArray)
                yield loadData();
            return true;
        }
        else {
            if (!props.disableNotifications)
                alert.error(error || 'Unable to add item. Something went wrong');
            return false;
        }
    }), [isEdit, props, toast, alert, loadData, isArray]);
    const deleteItem = useCallback((index, item) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!isEdit || !props.onDeleteItem)
            return true;
        const { success, error } = yield props.onDeleteItem(item);
        if (success) {
            if (!props.disableNotifications)
                toast.success(((_a = props.labels) === null || _a === void 0 ? void 0 : _a.deleted) || 'Item deleted');
            setData(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
            return true;
        }
        else {
            if (!props.disableNotifications)
                alert.error(error || 'Unable to delete item. Something went wrong');
            return false;
        }
    }), [isEdit, props, toast, alert]);
    const handleReOrder = useCallback((items) => __awaiter(void 0, void 0, void 0, function* () {
        if (!props.onReorderItems)
            return;
        setData(items);
        const { success, error } = yield props.onReorderItems(items);
        if (success && !props.disableNotifications)
            toast.success('Saved!');
        else if (!success && !props.disableNotifications)
            alert.error(error || 'Unable to reorder items');
        yield loadData();
    }), [props, alert, loadData, toast]);
    const tableData = isPaginated ? props.data : data;
    const tableTotal = isPaginated ? props.total : total;
    const newItemAdded = data.find(item => item.__newitem__);
    const hasAddCallback = !!props.onClickAddNew || !!props.onAddItem;
    const shouldShowAddButton = !newItemAdded && hasAddCallback && (props.showAddButton !== false);
    const handleAddButtonClick = useCallback(() => {
        if (props.onClickAddNew)
            props.onClickAddNew();
        else
            addNewItem();
    }, [props, addNewItem]);
    const commonProps = {
        columns: props.columns,
        pageSize: props.pageSize || 10,
        total: tableTotal,
        noItemsMessage: props.noDataText,
        disablePagination: props.disablePagination,
        // array data is set synchronously, so the caller's loading prop is the only
        // meaningful signal - internal loading only tracks the async data() variant
        loading: isArray ? props.loading || false : loading,
        search: Object.assign(Object.assign({}, props.search), { enable: ((_a = props === null || props === void 0 ? void 0 : props.search) === null || _a === void 0 ? void 0 : _a.enable) == false ? false : true }),
        filters: props.filters,
        actionButtons: props.actions,
        addNewRow: shouldShowAddButton ? (_jsx("span", { className: 'uxpcore_simpleconfigtable__addnew', onClick: handleAddButtonClick, children: props.addButtonLabel || `+ ${((_b = props.labels) === null || _b === void 0 ? void 0 : _b.add) || 'Add New'}` })) : undefined,
    };
    const table = useMemo(() => {
        if (isEdit) {
            return (_jsx(TableComponent, Object.assign({}, commonProps, { data: tableData, editable: {
                    onCancelNewItem: cancelNewItem,
                    onUpdateItem: saveItem,
                    onAddItem: saveNewItem,
                    onDeleteItem: deleteItem,
                    onReorderItems: props.onReorderItems ? handleReOrder : undefined,
                    labels: props.labels
                } })));
        }
        return (_jsx(TableComponent, Object.assign({}, commonProps, { data: tableData, onClickRow: props.onClickRow, onClickColumn: props.onClickColumn, editColumn: props.editColumn, deleteColumn: props.deleteColumn, editable: props.onReorderItems ? { onReorderItems: handleReOrder } : undefined })));
    }, [
        props.columns, props.noDataText, props.pageSize, props.disablePagination,
        props.search, props.filters, props.actions,
        loading, tableData, tableTotal, isEdit,
        cancelNewItem, saveItem, saveNewItem, deleteItem, handleReOrder, props,
        shouldShowAddButton, handleAddButtonClick
    ]);
    return (_jsx("div", { className: 'uxpcore_simpleconfigtable', children: table }));
};
// ============================================================================
// INTERNAL WRAPPER (Handles both legacy and new props)
// ============================================================================
const SimpleConfigurationTableInternal = (props) => {
    const isLegacy = 'uxpContext' in props && props.uxpContext !== undefined;
    if (isLegacy)
        return _jsx(SimpleConfigurationTableComponent, Object.assign({}, adaptLegacyProps(props)));
    return _jsx(SimpleConfigurationTableComponent, Object.assign({}, props));
};
/**
 * @export
 * A versatile table component with flexible feature-based configuration.
 * All chrome (search, filters, action buttons, pagination) is handled by the
 * underlying TableComponent — SCT is a pure data orchestration wrapper.
 */
export const SimpleConfigurationTable = memo(SimpleConfigurationTableInternal);
